// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 599efae8-3a8b-4332-b14d-30f9208d4619

// ---------------------------------------------------------------------------
// EAMS / CMDB — Schema-as-data
// ---------------------------------------------------------------------------
// This file is the "flexible schema" engine. Fact Sheet types, their sections,
// and their fields are DATA, not hardcoded JSX. That is what lets us (and later,
// end users per-tenant) add custom fields and relationship types without a code
// change — the LeanIX FactSheet superpower.
//
// The taxonomy below was reverse-engineered from a live LeanIX tenant
// (Northeast Georgia Health System) — see the Application fact sheets for
// "3DR Labs DiCellerator" and "Airstrip One". It encodes Optimum's cloud-
// migration discovery methodology (Cloud Transformation sections, per-team
// ownership + % complete, TIME / 6R disposition, DR/BC, PHI, RTO/RPO, etc.).
//
// When we move to AWS, this same object graph drives the DynamoDB item shapes
// and the API contract — the UI never needs to know field names ahead of time.
// ---------------------------------------------------------------------------

// Field data types the renderer + editor understand.
export const FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  NUMBER: 'number',
  CURRENCY: 'currency',
  SELECT: 'select',
  MULTISELECT: 'multiselect',
  BOOLEAN: 'boolean',
  DATE: 'date',
  RATING: 'rating', // 1..4 stars (LeanIX Functional/Technical Fit)
  ARROWS: 'arrows', // 1..4 criticality arrows (LeanIX Business Criticality)
  PERCENT: 'percent',
  LIFECYCLE: 'lifecycle', // active-from date rendered as a timeline bar
  URL: 'url',
  RELATION: 'relation', // link to other fact sheets (relationType key)
  TAGS: 'tags',
};

// ---------------------------------------------------------------------------
// Fact Sheet types (the 13 LeanIX types; counts are the NGHS production tenant)
// ---------------------------------------------------------------------------
export const FACT_SHEET_TYPES = [
  { key: 'Application',        label: 'Application',         badge: 'A', color: '#1565C0' },
  // A Deployment is a specific INSTANCE of an application — Epic PRD, Epic TST,
  // Epic TRN, or a one-off analyst build. Servers belong to a deployment, not to
  // the application in the abstract, and version/patch level is a property of the
  // instance rather than of the app. Without this layer a 7,000-server estate can
  // only be mapped to "which app", which is not a migratable unit of work: you cut
  // over Epic TST on a different date, with different risk, than Epic PRD.
  { key: 'Deployment',        label: 'Deployment',          badge: 'DP',color: '#0277BD' },
  { key: 'ITComponent',       label: 'IT Component',        badge: 'C', color: '#00838F' },
  { key: 'Interface',         label: 'Interface',           badge: 'I', color: '#6A1B9A' },
  { key: 'DataObject',        label: 'Data Object',         badge: 'D', color: '#8E24AA' },
  { key: 'BusinessCapability',label: 'Business Capability',  badge: 'B', color: '#283593' },
  { key: 'BusinessContext',   label: 'Business Context',    badge: 'X', color: '#5E35B1' },
  { key: 'Organization',      label: 'Organization',        badge: 'O', color: '#1976D2' },
  { key: 'Provider',          label: 'Provider',            badge: 'P', color: '#EF6C00' },
  { key: 'TechCategory',      label: 'Tech Category',       badge: 'T', color: '#00695C' },
  { key: 'Platform',          label: 'Platform',            badge: 'PL',color: '#2E7D32' },
  { key: 'Initiative',        label: 'Initiative',          badge: 'IN',color: '#43A047' },
  { key: 'Objective',         label: 'Objective',           badge: 'OB',color: '#7CB342' },
  { key: 'Contract',          label: 'Contract',            badge: 'CN',color: '#C62828' },
];

export const typeMeta = (key) =>
  FACT_SHEET_TYPES.find((t) => t.key === key) || { key, label: key, badge: '?', color: '#607D8B' };

// ---------------------------------------------------------------------------
// Picklists (LeanIX single/multi-select values). Editable per-tenant later.
// ---------------------------------------------------------------------------
export const PICKLISTS = {
  lifecycle: ['Plan', 'Phase In', 'Active', 'Phase Out', 'End of Life'],
  time: ['Tolerate', 'Invest', 'Migrate', 'Eliminate'],
  sixR: ['Rehost', 'Replatform', 'Repurchase', 'Refactor', 'Retire', 'Retain'],
  drTier: ['DR Tier: 1', 'DR Tier: 2', 'DR Tier: 3', 'DR Tier: 4'],
  criticality: ['Administrative Service', 'Business Operational', 'Business Critical', 'Mission Critical'],
  functionalFit: ['Unreasonable', 'Insufficient', 'Appropriate', 'Perfect'],
  technicalFit: ['Inappropriate', 'Unreasonable', 'Adequate', 'Fully Appropriate'],
  hostingType: ['On Premise', 'SaaS', 'IaaS', 'PaaS', 'Hybrid', 'Cloud'],
  qualitySeal: ['Draft', 'Broken', 'Approved', 'Rejected'],
  migrationWave: ['Wave 1', 'Wave 2', 'Wave 3', 'Not Scheduled'],
  migrationEffort: ['Small: 1-2 months', 'Medium: 3-4 months', 'Large: 5+ months'],
  cmDisposition: ['Rehost', 'Replatform', 'Repurchase', 'Refactor', 'Retire', 'Retain'],
  patientSafety: [
    'Low Risk - BC plans in place',
    'Medium Risk - BC plans inadequate',
    'High Risk - No BC plans',
  ],
  rto: ['< 8 Hours', '8 to 24 Hours', '24 to 96 Hours', '> 96 Hours'],
  rpo: ['< 4 hours', '4 to 8 hours', '8 to 24 Hours', '> 24 Hours'],
  targetDrStrategy: ['Backup & Restore', 'Pilot Light', 'Warm Standby', 'Multi-Site Active/Active'],
  applicationType: ['Clinical', 'Business', 'Enterprise', 'Ancillary', 'Infrastructure', 'Field Services'],
  tier: ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'],
  locationType: ['On-Prem Data Center', 'Co-location', 'AWS', 'Azure', 'Vendor Cloud (SaaS)', 'Hybrid'],
  // Governed list, but deliberately open-ended: real estates are full of
  // instances that fit no standard name ("Bob's build", a vendor validation
  // copy, a frozen pre-upgrade clone). Those are exactly the ones that get
  // missed in a migration, so the model has to be able to name them rather
  // than force them into the wrong bucket.
  environment: [
    'Production', 'Disaster Recovery', 'Isolated Recovery',
    'Test', 'Development', 'Training', 'Staging / Pre-Prod',
    'Sandbox', 'Validation', 'Reporting', 'Decommissioned', 'Other',
  ],
  // WHERE a deployment is in its life, kept apart from WHICH environment it
  // is. During a cloud migration the same application deliberately holds two
  // deployments of the same environment at once — "Epic PRD (on-prem)" Active
  // while "Epic PRD (Azure)" is Building — and at cutover the old one becomes
  // Decommissioned and STAYS, as history, rather than being deleted into the
  // 30-day trash (which purges; a decommissioned instance is a record, not
  // rubbish). An empty value reads as Active, so existing rows keep meaning
  // what they always meant. ('Decommissioned' also appears in the environment
  // list above — a pre-existing conflation left for compatibility; this field
  // is the one that should carry that fact going forward.)
  deploymentLifecycle: ['Planned', 'Building', 'Active', 'Decommissioned'],
};

// ---------------------------------------------------------------------------
// Relationship types (LeanIX relations). Each relation targets a fact-sheet type.
// Stored on a fact sheet as relations[relKey] = [{ id, ...meta }].
// ---------------------------------------------------------------------------
export const RELATION_TYPES = [
  { key: 'businessCapabilities', label: 'Business Capabilities', target: 'BusinessCapability', section: 'Business Support' },
  { key: 'businessContexts',     label: 'Business Contexts',     target: 'BusinessContext',    section: 'Business Support' },
  { key: 'organizations',        label: 'Organizations',        target: 'Organization',       section: 'Business Support' },
  { key: 'dataObjects',          label: 'Data Objects',         target: 'DataObject',          section: 'Data Management' },
  { key: 'itComponents',         label: 'IT Components',        target: 'ITComponent',         section: 'Data Management' },
  // Application -> its instances, and each instance -> the servers it runs on.
  // `application` is `single` because a deployment belongs to exactly one app;
  // a server, by contrast, can legitimately be shared across deployments, so
  // `servers`/`deployments` stay many-to-many.
  { key: 'deployments',          label: 'Deployments',          target: 'Deployment',          section: 'Dependencies' },
  { key: 'application',          label: 'Application',          target: 'Application',         section: 'Dependencies', single: true },
  { key: 'servers',              label: 'Servers',              target: 'ITComponent',         section: 'Dependencies' },
  { key: 'hostedDeployments',    label: 'Hosted Deployments',   target: 'Deployment',          section: 'Dependencies' },
  { key: 'providedInterfaces',   label: 'Provided Interfaces',  target: 'Interface',           section: 'Cloud Transformation' },
  { key: 'consumedInterfaces',   label: 'Consumed Interfaces',  target: 'Interface',           section: 'Cloud Transformation' },
  { key: 'parent',               label: 'Parent',               target: 'Application',         section: 'Dependencies', single: true },
  { key: 'children',             label: 'Children',             target: 'Application',         section: 'Dependencies' },
  { key: 'successors',           label: 'Successors',           target: 'Application',         section: 'Information' },
  { key: 'predecessors',         label: 'Predecessors',         target: 'Application',         section: 'Information' },
  { key: 'contracts',            label: 'Contracts',            target: 'Contract',            section: 'Dependencies' },
  { key: 'platforms',            label: 'Platforms',            target: 'Platform',            section: 'Business Support' },
  { key: 'initiatives',          label: 'Initiatives',          target: 'Initiative',          section: 'Projects' },
  // RelationEditor has always offered `objectives` on an Initiative, but there
  // was no such relation type, and Initiative uses simpleSchema (which declares
  // none) - so `.filter(Boolean)` dropped it silently and an Initiative could
  // never be linked to an Objective at all. The Objective fact-sheet type
  // exists; this is the edge that was missing.
  { key: 'objectives',           label: 'Objectives',           target: 'Objective',           section: 'Projects' },
  { key: 'providers',            label: 'Providers',            target: 'Provider',            section: 'Data Management' },
  // Business Capability hierarchy (the LeanIX capability map). Deliberately
  // ONE direction only: `parent`/`children` on Application declare both edges
  // and rely on whoever is editing to keep them in sync, which nothing in this
  // codebase actually enforces. A capability tree built from two independently
  // editable relations can disagree with itself; built from a single `single`
  // parent link, "who are my children" is instead answered by asking every
  // other capability "is this your parent", which cannot drift because there
  // is only one place the fact is recorded. CapabilityMap.js is the reader.
  { key: 'parentCapability',     label: 'Parent Capability',    target: 'BusinessCapability',  section: 'Hierarchy', single: true },
];

export const relationMeta = (key) => RELATION_TYPES.find((r) => r.key === key);

// ---------------------------------------------------------------------------
// APPLICATION fact-sheet schema — sections → fields (from the LeanIX PDFs).
// team = the responsible team (LeanIX shows "(App Team)", "(InfoSec Team)"...).
// Each Cloud Transformation subsection has a `complete` percent field.
// ---------------------------------------------------------------------------
export const APPLICATION_SCHEMA = {
  type: 'Application',
  sections: [
    {
      key: 'information',
      label: 'Information',
      fields: [
        { key: 'name', label: 'Name', type: FIELD_TYPES.TEXT, mandatory: true },
        { key: 'description', label: 'Description', type: FIELD_TYPES.TEXTAREA, mandatory: true },
        // Same catalog app, different deployed version per client/instance.
        { key: 'version', label: 'Version', type: FIELD_TYPES.TEXT },
        { key: 'catalogId', label: 'Catalog Entry (reference)', type: FIELD_TYPES.TEXT },
        { key: 'lifecycle', label: 'Lifecycle', type: FIELD_TYPES.LIFECYCLE },
        { key: 'lifecycleState', label: 'Lifecycle State', type: FIELD_TYPES.SELECT, picklist: 'lifecycle', mandatory: true },
        { key: 'tco', label: 'Total Cost of Ownership', type: FIELD_TYPES.CURRENCY },
        { key: 'mcpServer', label: 'MCP Server', type: FIELD_TYPES.TEXT },
      ],
    },
    {
      // Core CMDB dimensions — you filter/group/chart by these every day.
      key: 'classification',
      label: 'Classification & Ownership',
      fields: [
        { key: 'applicationType', label: 'Type', type: FIELD_TYPES.SELECT, picklist: 'applicationType' },
        { key: 'drTier', label: 'DR Tier', type: FIELD_TYPES.SELECT, picklist: 'tier' },
        // hostingType + vendorName reuse the same keys as the deeper Cloud
        // Transformation subsections, so the value is shared, shown up top too.
        { key: 'hostingType', label: 'Hosting Type (On-Prem / SaaS / IaaS …)', type: FIELD_TYPES.SELECT, picklist: 'hostingType' },
        { key: 'locationType', label: 'Current Hosting Location', type: FIELD_TYPES.TEXT },
        { key: 'targetHosting', label: 'Desired Future Hosting', type: FIELD_TYPES.TEXT },
        { key: 'applicationOwner', label: 'Business Owner', type: FIELD_TYPES.TEXT },
        { key: 'technicalOwner', label: 'Technical Owner', type: FIELD_TYPES.TEXT },
        { key: 'vendorName', label: 'Vendor', type: FIELD_TYPES.TEXT },
        { key: 'businessUnit', label: 'Functional Business Unit', type: FIELD_TYPES.TEXT },
      ],
    },
    {
      // Optimum's value-add: the migration knowledge base that grows per app.
      key: 'migrationKnowledge',
      label: 'Migration Knowledge',
      fields: [
        { key: 'appPurpose', label: 'What the application does', type: FIELD_TYPES.TEXTAREA },
        { key: 'lessonsLearned', label: 'Lessons Learned', type: FIELD_TYPES.TEXTAREA },
        { key: 'mustWhitelist', label: 'Must Whitelist (IPs / URLs / ports)', type: FIELD_TYPES.TEXTAREA },
        { key: 'awsGotchas', label: 'AWS Configuration Gotchas', type: FIELD_TYPES.TEXTAREA },
        { key: 'azureGotchas', label: 'Azure Configuration Gotchas', type: FIELD_TYPES.TEXTAREA },
      ],
    },
    {
      key: 'businessSupport',
      label: 'Business Support',
      fields: [
        { key: 'businessCriticality', label: 'Business Criticality', type: FIELD_TYPES.ARROWS, picklist: 'criticality' },
        { key: 'functionalFit', label: 'Functional Fit', type: FIELD_TYPES.RATING, picklist: 'functionalFit' },
      ],
      relations: ['businessCapabilities', 'businessContexts', 'organizations', 'platforms'],
    },
    {
      key: 'dataManagement',
      label: 'Data Management',
      fields: [],
      relations: ['dataObjects'],
    },
    {
      // LeanIX puts Technical Fit + IT Components under "Sourcing".
      key: 'sourcing',
      label: 'Sourcing',
      fields: [
        { key: 'technicalFit', label: 'Technical Fit', type: FIELD_TYPES.RATING, picklist: 'technicalFit' },
      ],
      relations: ['itComponents'],
    },
    {
      key: 'portfolioStrategy',
      label: 'Portfolio Strategy',
      fields: [
        { key: 'timeClassification', label: 'TIME Classification', type: FIELD_TYPES.SELECT, picklist: 'time' },
        { key: 'sixRClassification', label: '6R Classification', type: FIELD_TYPES.SELECT, picklist: 'sixR' },
      ],
    },
    {
      key: 'cloudTransformation',
      label: 'Cloud Transformation',
      tracksCompletion: true,
      subsections: [
        {
          key: 'cmGeneral', label: 'Cloud Migration General', team: 'App Team', complete: 'pctCmGeneral',
          fields: [
            { key: 'cmCurrentStatus', label: 'CM: Current Status', type: FIELD_TYPES.SELECT, picklist: ['Live', 'Retired', 'In Migration'] },
            { key: 'cmDisposition', label: 'CM: Disposition', type: FIELD_TYPES.SELECT, picklist: 'cmDisposition' },
            { key: 'cmWave', label: 'CM: Selected for Wave', type: FIELD_TYPES.SELECT, picklist: 'migrationWave' },
            { key: 'cmEffort', label: 'CM: Migration Effort', type: FIELD_TYPES.SELECT, picklist: 'migrationEffort' },
            { key: 'cmOtherIntegrations', label: 'CM: Other Integrations?', type: FIELD_TYPES.TEXT },
            { key: 'cmExternalIntegrations', label: 'CM: External Integrations?', type: FIELD_TYPES.TEXT },
          ],
        },
        {
          key: 'vendorInfo', label: 'Vendor Information & Cloud Migration Readiness', team: 'App Team', complete: 'pctVendor',
          fields: [
            { key: 'vendorName', label: 'Vendor Name', type: FIELD_TYPES.TEXT },
            { key: 'vendorWebsite', label: 'Vendor Website', type: FIELD_TYPES.URL },
            { key: 'vendorContact', label: 'Vendor Point of Contact', type: FIELD_TYPES.TEXT },
            { key: 'vendorEmail', label: 'Vendor Email', type: FIELD_TYPES.TEXT },
            { key: 'cmSaaS', label: 'CM: SaaS Solution', type: FIELD_TYPES.BOOLEAN },
            { key: 'cmIaaS', label: 'CM: IaaS Support', type: FIELD_TYPES.BOOLEAN },
            { key: 'cmRdsOrEc2', label: 'CM: RDS or EC2?', type: FIELD_TYPES.SELECT, picklist: ['RDS', 'EC2'] },
            { key: 'cmMinRam', label: 'CM: Min RAM for SQL', type: FIELD_TYPES.TEXT },
            { key: 'cmVendorReadiness', label: 'CM: Vendor Readiness', type: FIELD_TYPES.BOOLEAN },
          ],
        },
        {
          key: 'hosting', label: 'Hosting', team: 'App Team', complete: 'pctHosting',
          fields: [
            { key: 'hostingType', label: 'Hosting Type', type: FIELD_TYPES.SELECT, picklist: 'hostingType', mandatory: true },
            { key: 'hostingDescription', label: 'Hosting Description', type: FIELD_TYPES.TEXTAREA },
            // Frank, 2026-08-19: "we could have 2 fields that might show in the
            // line, the public and private URL of the app, since it might have
            // 5 app servers behind a LB it still needs the association."
            //
            // This is the piece the server relations cannot express. Five app
            // servers behind a load balancer are five ITComponent rows, and
            // none of them is the address a person types. The URL is what
            // identifies the application to everyone who is not an engineer,
            // it is what a migration has to keep working, and it is the join
            // between "the thing users call Hyperspace" and the hosts under
            // it. Kept as two fields rather than one because the internal name
            // is usually the one that breaks in a migration and the external
            // one is what the client notices.
            { key: 'publicUrl', label: 'Public URL', type: FIELD_TYPES.URL },
            { key: 'privateUrl', label: 'Internal URL', type: FIELD_TYPES.URL },
          ],
        },
        {
          key: 'sso', label: 'Single Sign-On', team: 'App Team', complete: 'pctSso',
          fields: [
            { key: 'ssoAvailability', label: 'SSO - Availability', type: FIELD_TYPES.SELECT, picklist: ['Available', 'Not Available'] },
            { key: 'ssoProviders', label: 'SSO - Available Providers', type: FIELD_TYPES.TEXT },
          ],
        },
        {
          key: 'currentTechArch', label: 'Current Technical Architecture', team: 'DB & Ops Teams', complete: 'pctTechArch',
          fields: [
            { key: 'serverInventory', label: 'CM: Server Inventory', type: FIELD_TYPES.TEXTAREA },
            { key: 'osVersion', label: 'CM: Operating System / Version', type: FIELD_TYPES.TEXT },
            { key: 'dbType', label: 'CM: Database Type & Version', type: FIELD_TYPES.TEXT },
            { key: 'cmRdsCompliant', label: 'CM: Database compliant with AWS RDS?', type: FIELD_TYPES.BOOLEAN },
          ],
        },
        {
          key: 'drbc', label: 'Current Disaster Recovery & Business Continuity', team: 'App Team', complete: 'pctDrBc',
          fields: [
            { key: 'primaryStakeholder', label: 'Primary Stakeholder', type: FIELD_TYPES.TEXT, mandatory: true },
            { key: 'primaryStakeholderDirector', label: 'Primary Stakeholder - Director', type: FIELD_TYPES.TEXT, mandatory: true },
            { key: 'primaryUserRole', label: 'Primary User Role', type: FIELD_TYPES.TEXT },
            { key: 'downtimeProcedures', label: 'Down time procedures exist?', type: FIELD_TYPES.BOOLEAN },
            { key: 'remoteWork', label: 'Can work be performed remotely?', type: FIELD_TYPES.BOOLEAN },
            { key: 'patientSafetyImpact', label: 'Patient Safety Impact', type: FIELD_TYPES.SELECT, picklist: 'patientSafety' },
            { key: 'rto', label: 'RTO', type: FIELD_TYPES.SELECT, picklist: 'rto' },
            { key: 'rpo', label: 'RPO', type: FIELD_TYPES.SELECT, picklist: 'rpo' },
            { key: 'phi', label: 'Contains PHI', type: FIELD_TYPES.BOOLEAN },
          ],
        },
        {
          key: 'networking', label: 'Networking & Connectivity', team: 'Network Team', complete: 'pctNetwork',
          fields: [
            { key: 'hardcodedIps', label: 'CM: Hardcoded IP addresses?', type: FIELD_TYPES.BOOLEAN },
            { key: 'latencySensitive', label: 'CM: Latency Sensitivity', type: FIELD_TYPES.BOOLEAN },
            { key: 'firewallRules', label: 'CM: Firewall Rules and Ports', type: FIELD_TYPES.TEXTAREA },
            { key: 'vpnRequired', label: 'CM: VPN access required', type: FIELD_TYPES.BOOLEAN },
          ],
        },
        {
          key: 'authAccess', label: 'Authentication & Access', team: 'InfoSec Team', complete: 'pctAuth',
          fields: [
            { key: 'clientAccess', label: 'CM: Client Access', type: FIELD_TYPES.TEXT },
            { key: 'userAuth', label: 'CM: User authentication required', type: FIELD_TYPES.TEXT },
            { key: 'mfa', label: 'CM: MFA?', type: FIELD_TYPES.TEXT },
          ],
        },
        {
          key: 'security', label: 'Security and Compliance', team: 'InfoSec Team', complete: 'pctSecurity',
          fields: [
            { key: 'securityControls', label: 'CM: Security Controls', type: FIELD_TYPES.TEXT },
            { key: 'complianceStandards', label: 'CM: Compliance Standards', type: FIELD_TYPES.TEXT },
            { key: 'auditRequirements', label: 'CM: Audit Requirements', type: FIELD_TYPES.TEXT },
          ],
        },
        {
          key: 'performance', label: 'Performance & Capacity', team: 'App Team', complete: 'pctPerformance',
          fields: [
            { key: 'normalHours', label: 'Normal Hours', type: FIELD_TYPES.TEXT },
            { key: 'peakPeriods', label: 'Peak Periods', type: FIELD_TYPES.TEXT },
          ],
        },
        {
          key: 'postMigration', label: 'Post Migration Operations', team: 'App Team', complete: 'pctPostMigration',
          fields: [
            { key: 'monitoringAlerting', label: 'CM: Monitoring & Alerting', type: FIELD_TYPES.TEXT },
            { key: 'targetDrStrategy', label: 'CM: Target DR Strategy', type: FIELD_TYPES.SELECT, picklist: 'targetDrStrategy' },
          ],
        },
      ],
      relations: ['providedInterfaces', 'consumedInterfaces'],
    },
    {
      key: 'dependencies',
      label: 'Dependencies',
      fields: [],
      relations: ['parent', 'children', 'contracts'],
    },
    {
      key: 'governance',
      label: 'Governance',
      fields: [
        { key: 'aiGovernance', label: 'AI Governance and Adoption', type: FIELD_TYPES.TEXTAREA },
        { key: 'aiRiskReduction', label: 'AI Agent Business Value — Expected Risk Reduction', type: FIELD_TYPES.CURRENCY },
      ],
    },
    {
      key: 'projects',
      label: 'Projects',
      fields: [],
      relations: ['initiatives'],
    },
    {
      key: 'documents',
      label: 'Documents & Links',
      fields: [
        // Link back to source-of-truth systems (no data duplication) + S3 uploads.
        { key: 'sharePointUrl', label: 'SharePoint Site / Doc', type: FIELD_TYPES.URL },
        { key: 'confluenceUrl', label: 'Confluence Page', type: FIELD_TYPES.URL },
        { key: 'serviceNowCi', label: 'ServiceNow CMDB CI', type: FIELD_TYPES.TEXT },
      ],
    },
  ],
};

// Lightweight schemas for the other fact-sheet types (name + description +
// lifecycle by default; extend as needed / per tenant).
const simpleSchema = (type, extraFields = []) => ({
  type,
  sections: [
    {
      key: 'information',
      label: 'Information',
      fields: [
        { key: 'name', label: 'Name', type: FIELD_TYPES.TEXT, mandatory: true },
        { key: 'description', label: 'Description', type: FIELD_TYPES.TEXTAREA },
        { key: 'lifecycleState', label: 'Lifecycle State', type: FIELD_TYPES.SELECT, picklist: 'lifecycle' },
        ...extraFields,
      ],
    },
  ],
});

// ---------------------------------------------------------------------------
// BUSINESS CAPABILITY — the EAM+APM spine. What the business DOES, arranged
// as a hierarchy, so applications can be argued about by what they support
// rather than as a flat list. No `level` field: depth is CapabilityMap.js
// reading the `parentCapability` hierarchy, and a stored level next to a
// derived one is exactly the two-sources-of-truth shape that has bitten this
// codebase before (see DR Tier, tag vs field, in resolveDrTier above) — only
// add it if something someday needs to override the computed depth by hand.
// ---------------------------------------------------------------------------
export const BUSINESS_CAPABILITY_SCHEMA = {
  type: 'BusinessCapability',
  sections: [
    {
      key: 'information',
      label: 'Information',
      fields: [
        { key: 'name', label: 'Name', type: FIELD_TYPES.TEXT, mandatory: true },
        { key: 'description', label: 'Description', type: FIELD_TYPES.TEXTAREA },
        { key: 'lifecycleState', label: 'Lifecycle State', type: FIELD_TYPES.SELECT, picklist: 'lifecycle' },
      ],
    },
    {
      key: 'hierarchy',
      label: 'Hierarchy',
      fields: [],
      relations: ['parentCapability'],
    },
    {
      // Pre-existing, previously reachable only through RelationEditor's
      // EXTRA_RELATION_SOURCES fallback map (which applies only when a type
      // declares no relations of its own). Declaring `hierarchy` above would
      // otherwise have silently taken these two away.
      key: 'businessSupport',
      label: 'Business Support',
      fields: [],
      relations: ['businessCapabilities', 'organizations'],
    },
  ],
};

export const SCHEMAS = {
  Application: APPLICATION_SCHEMA,
  BusinessCapability: BUSINESS_CAPABILITY_SCHEMA,
  ITComponent: simpleSchema('ITComponent', [
    { key: 'category', label: 'Category', type: FIELD_TYPES.TEXT },
    // Real obsolescence risk needs an actual date, not only a date inferred
    // from an OS string. eosDate is the vendor "no more patches" date and is
    // read FIRST by Obsolescence.js; eolDate (hardware/product retirement) is
    // the fallback when no eosDate is recorded; extendedSupportEnd is a paid
    // extension (Microsoft ESU, Red Hat ELS, SUSE LTSS...) and is surfaced as
    // its own flag rather than folded into the primary classification.
    // lifecycleSource records where the three dates above came from (vendor
    // matrix, manual entry, import) so a client challenging a number can be
    // pointed at its provenance.
    { key: 'eolDate', label: 'End of Life', type: FIELD_TYPES.DATE },
    { key: 'eosDate', label: 'End of Support', type: FIELD_TYPES.DATE },
    { key: 'extendedSupportEnd', label: 'Extended Support Ends', type: FIELD_TYPES.DATE },
    { key: 'lifecycleSource', label: 'Lifecycle Source', type: FIELD_TYPES.TEXT },
  ]),
  Deployment: simpleSchema('Deployment', [
    { key: 'environment', label: 'Environment', type: FIELD_TYPES.SELECT, picklist: 'environment', mandatory: true },
    // Free text alongside the picklist so a non-standard instance keeps its real
    // name instead of disappearing into "Other".
    { key: 'environmentLabel', label: 'Environment Label (if Other)', type: FIELD_TYPES.TEXT },
    { key: 'lifecycleStatus', label: 'Lifecycle', type: FIELD_TYPES.SELECT, picklist: 'deploymentLifecycle' },
    { key: 'version', label: 'Version', type: FIELD_TYPES.TEXT },
    { key: 'patchLevel', label: 'Patch / Build Level', type: FIELD_TYPES.TEXT },
    { key: 'upgradeCadence', label: 'Upgrade Cadence', type: FIELD_TYPES.TEXT },
    { key: 'locationType', label: 'Location', type: FIELD_TYPES.SELECT, picklist: 'locationType' },
    { key: 'hostingType', label: 'Hosting', type: FIELD_TYPES.SELECT, picklist: 'hostingType' },
    { key: 'dataClassification', label: 'Contains PHI / Real Data', type: FIELD_TYPES.BOOLEAN },
    // Whether a non-prod instance holds real patient data decides both its
    // security posture and whether it can be refreshed from prod.
    { key: 'refreshedFrom', label: 'Refreshed From', type: FIELD_TYPES.TEXT },
    { key: 'migrationWave', label: 'Migration Wave', type: FIELD_TYPES.SELECT, picklist: 'migrationWave' },
    { key: 'cutoverDate', label: 'Planned Cutover', type: FIELD_TYPES.DATE },
    { key: 'goLiveDate', label: 'Go Live Date', type: FIELD_TYPES.DATE },
    { key: 'decommissionDate', label: 'Decommission Date', type: FIELD_TYPES.DATE },
    { key: 'owner', label: 'Instance Owner', type: FIELD_TYPES.TEXT },
    { key: 'notes', label: 'Notes', type: FIELD_TYPES.TEXTAREA },
  ]),
  Interface: simpleSchema('Interface', [
    { key: 'interfaceType', label: 'Interface Type', type: FIELD_TYPES.SELECT, picklist: ['TCP/IP', 'HL7', 'API', 'File Transfer'] },
  ]),
  DataObject: simpleSchema('DataObject'),
  BusinessContext: simpleSchema('BusinessContext'),
  Organization: simpleSchema('Organization', [
    { key: 'orgType', label: 'Subtype', type: FIELD_TYPES.SELECT, picklist: ['Team', 'Department', 'Business Unit'] },
  ]),
  Provider: simpleSchema('Provider'),
  TechCategory: simpleSchema('TechCategory'),
  Platform: simpleSchema('Platform'),
  Initiative: simpleSchema('Initiative'),
  Objective: simpleSchema('Objective'),
  Contract: simpleSchema('Contract', [
    { key: 'contractValue', label: 'Contract Value', type: FIELD_TYPES.CURRENCY },
    { key: 'renewalDate', label: 'Renewal Date', type: FIELD_TYPES.DATE },
  ]),
};

export const schemaFor = (type) => SCHEMAS[type] || simpleSchema(type);

// Resolve a picklist reference (string key → PICKLISTS[key], or inline array).
export const resolvePicklist = (picklist) =>
  Array.isArray(picklist) ? picklist : PICKLISTS[picklist] || [];

// Flatten all field defs for a type (incl. Cloud Transformation subsections).
// Some keys deliberately appear in two sections so the same value shows in both
// places (hostingType, vendorName). They must be returned ONCE: every caller
// treats this list as the field universe, so a duplicate silently inflates the
// denominator of every completion percentage in the product.
// ---------------------------------------------------------------------------
// Field definitions added at runtime by an administrator
// ---------------------------------------------------------------------------
// The schema above is the BASE model shipped with the product. Administrators
// can also add fields, and the backend already scopes them correctly: a field
// created by a master admin is stored with tenant_id NULL and therefore applies
// to EVERY workspace, while a field created inside a client workspace stays
// local to it. Existing records simply have no value for a new field, which is
// the point — it can be filled in later or left blank.
//
// Those definitions live in the database, and this module is synchronous and
// has no data access, so the store registers them here once reference data has
// loaded. Without this step a field could be added, stored, and then never
// appear on a single fact sheet — which is exactly what used to happen.
let RUNTIME_FIELDS = [];

export function registerFieldDefs(defs) {
  // Registering new defs is the ONLY thing that can change what allFieldsFor
  // returns, so it is also the only thing that may drop its cache.
  FIELDS_BY_TYPE.clear();
  RUNTIME_FIELDS = (defs || [])
    .filter((d) => d && d.key && d.entityType)
    .map((d) => ({
      key: d.key,
      label: d.label || d.key,
      type: d.dataType || FIELD_TYPES.TEXT,
      section: d.section || 'Additional Fields',
      subsection: d.subsection || null,
      picklist: d.picklist || d.picklistKey || undefined,
      mandatory: !!d.mandatory,
      entityType: d.entityType,
      // Carried through so the UI can say where a field came from. A field
      // somebody added is not the same thing as one the product ships with,
      // and a user deciding whether to trust or rename it needs to know which.
      addedByAdmin: true,
      global: d.tenantId == null,
      sortOrder: d.sortOrder || 0,
    }));
}

export const runtimeFieldsFor = (type) => RUNTIME_FIELDS.filter((f) => f.entityType === type);

// The answer for a given type changes only when an administrator's field defs
// are registered, but this is the hot path underneath every completion
// percentage in the product: `computeCompletion` calls it once per fact sheet,
// so a 4,000-row workspace rebuilt this ~90-entry list 4,000 times per
// navigation — a spread per field and a fresh Set each time, for an answer that
// was identical every time. Measured at 5.4 µs per call, that is 21 ms of pure
// re-derivation on each of the 47 call sites that list an inventory.
//
// The cached list and its entries are frozen: every caller treats this as the
// field universe and only reads it, and a caller that sorted it in place would
// otherwise corrupt the answer for everyone afterwards.
const FIELDS_BY_TYPE = new Map();

export const allFieldsFor = (type) => {
  const cached = FIELDS_BY_TYPE.get(type);
  if (cached) return cached;
  const schema = schemaFor(type);
  const out = [];
  for (const section of schema.sections) {
    (section.fields || []).forEach((f) => out.push({ ...f, section: section.label }));
    (section.subsections || []).forEach((sub) => {
      if (sub.complete) {
        out.push({ key: sub.complete, label: `% Complete - ${sub.label}`, type: FIELD_TYPES.PERCENT, section: section.label });
      }
      (sub.fields || []).forEach((f) => out.push({ ...f, section: `${section.label} / ${sub.label}` }));
    });
  }
  // Admin-added fields come LAST, so a base field always wins a key collision.
  // An added field can never shadow or redefine part of the shipped model —
  // the dedupe below keeps the first occurrence of each key.
  runtimeFieldsFor(type)
    .slice()
    .sort((a, b) => (a.sortOrder - b.sortOrder) || String(a.label).localeCompare(String(b.label)))
    .forEach((f) => out.push({ ...f }));

  const seen = new Set();
  const list = Object.freeze(
    out.filter((f) => (seen.has(f.key) ? false : seen.add(f.key))).map((f) => Object.freeze(f))
  );
  FIELDS_BY_TYPE.set(type, list);
  return list;
};

// Tags in LeanIX are grouped (Cell, DR Tier, Managed By, ...). We model a tag
// as { group, value } so facets can group them.
export const TAG_GROUPS = ['Cell', 'DR Tier', 'Managed By', 'Other'];

// ---------------------------------------------------------------------------
// DR Tier is recorded two ways in real data: as a field (`fields.drTier` =
// "Tier 2") and as a tag ("DR Tier: 2"). Imports, templates and hand-entry each
// favour a different one, so screens that read only one of them silently drop
// applications. Always resolve through here.
// ---------------------------------------------------------------------------
export function resolveDrTier(factSheet) {
  if (!factSheet) return '';
  const tag = (factSheet.tags || []).find((t) => t && t.group === 'DR Tier');
  const raw = (tag && tag.value) || (factSheet.fields && factSheet.fields.drTier) || '';
  const n = String(raw).match(/([1-4])/);
  return n ? `Tier ${n[1]}` : '';
}

// ---------------------------------------------------------------------------
// Canonical field aliases — ONE incoming header maps to exactly ONE field
// ---------------------------------------------------------------------------
// Clients never send a spreadsheet whose columns match our field names, so
// incoming headers are normalised and looked up here. This table used to live in
// ImportDialog.js, where three aliases each claimed TWO canonical fields:
//
//   'currentstatehostingscenario' -> hostingType AND locationType
//   'cmdisposition'               -> sixRClassification AND cmDisposition
//   'primarystakeholder'          -> applicationOwner AND primaryStakeholder
//
// Resolution was a .find(), so whichever entry happened to be declared first
// silently won and the other field could never be populated from an import.
// That is not theoretical: in the loaded client data it produced ~700 values
// stored twice under two different names —
//
//   DFCI  314 of 314 apps have hostingType identical to locationType
//   NGHS  179 of 181 apps have sixRClassification identical to cmDisposition
//   NGHS  213 of 213 apps have applicationOwner identical to primaryStakeholder
//
// which also makes a portfolio look like it has more distinct data than it does,
// and splits one concept across two columns when comparing clients.
//
// THE RULE, enforced by aliasCollisions() below: an alias that is the normalised
// form of a field's OWN key or label belongs to that field; generic aliases
// belong to the primary concept. Nothing may claim an alias twice.
// ---------------------------------------------------------------------------

export const normalizeHeader = (h) => String(h == null ? '' : h)
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

export const FIELD_ALIASES = {
  name: ['displayname', 'appname', 'applicationname', 'application'],
  description: ['dfcidescription', 'appdescription'],
  // "Current state hosting scenario" describes the hosting MODEL (SaaS/IaaS/
  // on-prem), which is hostingType. locationType is the physical/provider
  // location and keeps only its own unambiguous aliases.
  hostingType: ['currentstatehostingscenario', 'hosting'],
  locationType: ['hostinglocation', 'location'],
  targetHosting: ['desiredfuturehostinglocation', 'targetstatehostingrecommendation', 'futurehosting'],
  timeClassification: ['time'],
  sixRClassification: ['6rclassification', 'sixr', 'disposition'],
  cmDisposition: [], // owns 'cmdisposition' via its own key — no extra aliases
  drTier: ['disasterrecoverytier', 'dr'],
  applicationType: ['class'],
  businessCriticality: ['criticality'],
  applicationOwner: ['businessowner', 'owner'],
  primaryStakeholder: [], // owns 'primarystakeholder' via its own key
  primaryStakeholderDirector: ['director'],
  technicalOwner: ['itowner'],
  vendorName: ['applicationvendornameandcontact', 'vendor', 'supportgroup'],
  businessUnit: ['functionalbusinessunit', 'functionalarea', 'department'],
  // NOT 'lifecycle' — that is a real field key of its own (a LIFECYCLE-typed
  // active-from DATE), whereas lifecycleState is the Plan/Active/End-of-Life
  // picklist. aliasCollisions() caught this one before it shipped.
  lifecycleState: ['status', 'active'],
  ssoProviders: ['istheapplicationssoenabledifyesprovidethenameofthessoprovider', 'ssoprovider'],
  userAuth: ['whichauthenticationservicesdoestheapplicationuse', 'authentication'],
  lessonsLearned: ['optimumnotes', 'notes', 'questionnairestatusnotes', 'comments'],
  appPurpose: ['whattheapplicationdoes', 'purpose'],
};

// Every alias a field answers to: its own key, its own label, and the explicit
// list above. Built per fact-sheet type so labels stay authoritative.
export function aliasIndex(type = 'Application') {
  const index = new Map();
  const claim = (alias, key) => {
    const a = normalizeHeader(alias);
    if (!a) return;
    if (!index.has(a)) index.set(a, new Set());
    index.get(a).add(key);
  };
  const fields = allFieldsFor(type);
  const known = new Set(fields.map((f) => f.key));
  fields.forEach((f) => {
    claim(f.key, f.key);
    if (f.label) claim(f.label, f.key);
  });
  // FIELD_ALIASES is one global table, but a key in it is not a field on every
  // type. Claiming regardless produced two collisions on Deployment — which has
  // its own `owner` and `notes` fields, while the table pointed those same
  // headers at `applicationOwner` and `lessonsLearned`, neither of which exists
  // on Deployment. The importer then had to ask the user about two of the most
  // ordinary column names there are, and a header could resolve to a key the
  // type does not have.
  Object.entries(FIELD_ALIASES).forEach(([key, list]) => {
    if (!known.has(key)) return;
    (list || []).forEach((a) => claim(a, key));
  });
  return index;
}

// Any alias claimed by more than one field. MUST be empty — a non-empty result
// means an import would silently pick a winner and quietly drop the other field.
export function aliasCollisions(type = 'Application') {
  const out = [];
  aliasIndex(type).forEach((keys, alias) => {
    if (keys.size > 1) out.push({ alias, fields: [...keys].sort() });
  });
  return out.sort((a, b) => a.alias.localeCompare(b.alias));
}

// The rule above says the no-double-claim invariant is "enforced by
// aliasCollisions()", but nothing ever called it — so it was documented, not
// enforced, and the next alias added would have reintroduced M7 undetected.
// Check it once, at module load, for every type that has an alias table. This
// cannot fail a build (there is no test runner), so it does the next best thing:
// says exactly which alias is doubly claimed, loudly, in every environment.
(() => {
  try {
    const bad = FACT_SHEET_TYPES
      .map((t) => ({ type: t.key, collisions: aliasCollisions(t.key) }))
      .filter((x) => x.collisions.length);
    if (!bad.length) return;
    bad.forEach(({ type, collisions }) => {
      collisions.forEach(({ alias, fields }) => {
        // eslint-disable-next-line no-console
        console.error(
          `[schema] alias "${alias}" on ${type} is claimed by ${fields.length} fields `
          + `(${fields.join(', ')}). One header must map to exactly one field — the importer `
          + 'will refuse to guess and will ask the user instead. Fix FIELD_ALIASES.'
        );
      });
    });
  } catch (e) { /* never let a diagnostic break the app */ }
})();

// Resolve one incoming column header to a canonical field key, or '' when it is
// genuinely new. Returns the ambiguity rather than guessing when a header is
// claimed twice, so a caller can surface it instead of silently choosing.
export function resolveHeader(header, type = 'Application') {
  const a = normalizeHeader(header);
  if (!a) return { key: '', ambiguous: null };
  const hit = aliasIndex(type).get(a);
  if (!hit || hit.size === 0) return { key: '', ambiguous: null };
  if (hit.size > 1) return { key: '', ambiguous: [...hit].sort() };
  return { key: [...hit][0], ambiguous: null };
}

// Near-miss detector: is a proposed NEW custom field really an existing one
// wearing a different name? Catches "App Owner Name" against applicationOwner
// before it becomes cf_appownername and fragments the common field set.
export function suggestExistingField(header, type = 'Application') {
  const a = normalizeHeader(header);
  if (a.length < 4) return [];
  const scored = [];
  allFieldsFor(type).forEach((f) => {
    const k = normalizeHeader(f.key);
    const l = normalizeHeader(f.label);
    let score = 0;
    // Substring matching only against keys/labels long enough to be meaningful.
    // Without the length guard, short keys produce nonsense: 'tco' is a
    // substring of 'sproc-ketCOunt', so every unrelated column "matched" it.
    if (k.length >= 5 && (k.includes(a) || a.includes(k))) score = Math.max(score, 0.8);
    if (l.length >= 5 && (l.includes(a) || a.includes(l))) score = Math.max(score, 0.9);
    // Shared word stems: "owner", "vendor", "tier", "hosting"...
    const stems = ['owner', 'vendor', 'tier', 'host', 'critical', 'wave', 'disposition',
      'purpose', 'unit', 'stakeholder', 'lifecycle', 'contact', 'rto', 'rpo'];
    stems.forEach((s) => { if (a.includes(s) && (k.includes(s) || l.includes(s))) score = Math.max(score, 0.6); });
    if (score > 0) scored.push({ key: f.key, label: f.label, score });
  });
  return scored.sort((x, y) => y.score - x.score).slice(0, 4);
}

// Report values stored twice under different field names — the residue of the
// old ambiguous aliases. Reports only; merging is a deliberate admin action,
// never something an import does to client data behind the user's back.
export function duplicateValueReport(factSheets, pairs) {
  const known = pairs || [
    ['hostingType', 'locationType'],
    ['sixRClassification', 'cmDisposition'],
    ['applicationOwner', 'primaryStakeholder'],
  ];
  return known.map(([a, b]) => {
    let both = 0;
    let identical = 0;
    (factSheets || []).forEach((fs) => {
      const va = (fs.fields || {})[a];
      const vb = (fs.fields || {})[b];
      if (va && vb) {
        both += 1;
        if (String(va).trim() === String(vb).trim()) identical += 1;
      }
    });
    return { a, b, both, identical };
  }).filter((r) => r.both > 0);
}
